import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  getListSecretsQueryKey,
  useDeleteSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import {
  claimPendingOAuthConnection,
  clearPendingOAuthConnection,
  hasManagedOAuthReturn,
  isManagedOAuthReturn,
  MANAGED_OAUTH_RETURN_PARAM,
  readPendingOAuthConnection,
  type IPendingOAuthConnection,
} from '../oauth-resume';

/** What the connect wizard reopens with after an OAuth consent round trip. */
export interface IOAuthConnectionResume
  extends Omit<IPendingOAuthConnection, 'savedAt'> {
  /** Set when consent failed; the pending credential has been removed. */
  error?: string;
}

/** Where a managed-credential sign-in started. */
export type TManagedOAuthSource = 'create' | 'edit' | 'reconnect' | 'connect';

const sourceOf = (pending: IPendingOAuthConnection): TManagedOAuthSource =>
  pending.connectOnReturn
    ? 'connect'
    : pending.edit
      ? 'edit'
      : pending.reconnect
        ? 'reconnect'
        : 'create';

const withoutReturnParams = (params: URLSearchParams) => {
  const next = new URLSearchParams(params);
  next.delete('data');
  next.delete('error');
  next.delete(MANAGED_OAUTH_RETURN_PARAM);
  return next;
};

/** Arguments for {@link useManagedOAuthReturn}. */
export interface IUseManagedOAuthReturnArgs {
  /**
   * Only resume connections for this integration; omit to accept any. An
   * empty name (integration not loaded yet) matches nothing.
   */
  integrationName?: string;
  /**
   * Which returns to pick up: the connect wizard's (default), the edit
   * dialog's, Reconnect's, or those that write the connection on return.
   */
  source?: TManagedOAuthSource;
  /** Only pick up sign-ins started from this screen (see the pending `origin`). */
  origin?: string;
  /**
   * Called once with the wizard state to reopen. May return query params to
   * set (e.g. a selection), applied in the same URL update that clears the
   * return's params.
   */
  onResume: (resume: IOAuthConnectionResume) => Record<string, string> | void;
  /**
   * Leaves the return's params in the URL for someone else to consume (e.g. a
   * trigger dialog reopening on the same return).
   */
  keepReturnParams?: () => boolean;
}

/**
 * Picks up the browser's return from an OAuth provider for a connection the
 * wizard started with a managed credential. Gringotts redirects back with
 * `?data=<credential id>` or `?error=<reason>`; this strips them, drops the
 * pending credential when consent failed, and hands the saved wizard state to
 * `onResume`. The saved state is only cleared once the params are gone, so
 * classic `?data=` handlers on the same page keep skipping this return.
 *
 * @param args - Hook arguments.
 */
export function useManagedOAuthReturn({
  integrationName,
  source = 'create',
  origin,
  onResume,
  keepReturnParams,
}: IUseManagedOAuthReturnArgs) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteSecret } = useDeleteSecret2();
  const handled = useRef(false);

  // eslint-disable-next-line no-restricted-syntax -- reason: the OAuth return is an external navigation event detected from the URL on load
  useEffect(() => {
    const pending = readPendingOAuthConnection();
    if (!pending) {
      // Its sign-in expired: nothing to resume, so just tidy the URL.
      if (hasManagedOAuthReturn(searchParams)) {
        setSearchParams(withoutReturnParams(searchParams), { replace: true });
      }
      return;
    }
    if (
      integrationName !== undefined &&
      pending.integrationName !== integrationName
    ) {
      return;
    }
    if (sourceOf(pending) !== source) return;
    if (origin !== undefined && pending.origin !== origin) return;

    if (!searchParams.has('data') && !searchParams.has('error')) {
      if (handled.current) {
        clearPendingOAuthConnection();
        handled.current = false;
      }
      return;
    }
    // Only the marked return of this sign-in is ours, not BDK's own OAuth.
    if (!isManagedOAuthReturn(searchParams)) return;
    const error = searchParams.get('error');
    if (handled.current || !claimPendingOAuthConnection()) return;
    handled.current = true;

    // A new credential that never signed in is dropped; an existing one
    // signed in again keeps its previous sign-in.
    // The sign-in's own ids: the store may not match the URL yet on a return.
    const { organizationId, workspaceId } = pending;
    if (error && !pending.reconnect) {
      void deleteSecret({
        organizationId,
        workspaceId,
        id: pending.credentialId,
      }).catch(deleteError =>
        captureException(deleteError, {
          tags: {
            module: 'credentials',
            action: 'deletePendingOAuthCredential',
          },
        }),
      );
    }
    void queryClient.invalidateQueries({
      queryKey: getListSecretsQueryKey(organizationId),
    });

    const { savedAt: _savedAt, ...resume } = pending;
    const paramsToSet = onResume(error ? { ...resume, error } : resume);
    if (keepReturnParams?.()) return;

    // One update: separate ones would each start from this render's params.
    const next = withoutReturnParams(searchParams);
    Object.entries(paramsToSet ?? {}).forEach(([key, value]) =>
      next.set(key, value),
    );
    setSearchParams(next, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    integrationName,
    source,
    origin,
    deleteSecret,
    queryClient,
    onResume,
    keepReturnParams,
  ]);
}
